import {ComponentRef, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, TemplateRef} from '@angular/core';
import {ComponentPortal} from "@angular/cdk/portal";
import {ConnectionPositionPair, Overlay, OverlayPositionBuilder, OverlayRef} from '@angular/cdk/overlay';
import {CustomTooltipComponent} from "../components/custom-tooltip/custom-tooltip.component";

@Directive({
  selector: '[customToolTip]'
})
export class CustomTooltipDirective implements OnInit, OnDestroy {

  /**
   * This will be used to show tooltip or not
   * This can be used to show the tooltip conditionally
   */

  private _showToolTip: boolean = true;
  @Input('showToolTip') private set _setShowTooltip(show: boolean) {
    this._showToolTip = show;

    if (show) {
      this._show();
    } else {
      this._hide();
    }
  }

  //If this is specified then the specified text will be shown in the tooltip
  @Input(`customToolTip`) text: string;

  //If this is specified then specified template will be rendered in the tooltip
  @Input() contentTemplate: TemplateRef<any>;

  @Input() delay: number = 500;

  private _overlayRef: OverlayRef;
  private _tooltipRef: ComponentRef<CustomTooltipComponent>;
  private _timeout: any;

  constructor(private readonly _overlay: Overlay,
              private readonly _overlayPositionBuilder: OverlayPositionBuilder,
              private readonly _elementRef: ElementRef) { }

  /**
   * Init life cycle event handler
   */
  ngOnInit() {
    if (!this._showToolTip) {
      return;
    }

    //you can take the position as an input to adjust the position
    //, for now, it will show at the bottom always; but you can adjust your code
    // as per your need
    const positionStrategy = this._overlayPositionBuilder
      .flexibleConnectedTo(this._elementRef)
      .withPositions([
        new ConnectionPositionPair(
          { originX: 'center', originY: 'bottom' },
          { overlayX: 'center', overlayY: 'top' }),
        new ConnectionPositionPair(
          { originX: 'center', originY: 'top' },
          { overlayX: 'center', overlayY: 'bottom' })
      ]);

    this._overlayRef = this._overlay.create({ positionStrategy });
  }

  /**
   * This method will be called whenever the mouse enters in the Host element
   * i.e. where this directive is applied
   * This method will show the tooltip by instantiating the CustomToolTipComponent and attaching to the overlay
   */
  @HostListener('mouseenter')
  private _show(): void {
    //attach the component if it has not already attached to the overlay
    if (this._overlayRef && !this._overlayRef.hasAttached()) {

      this._timeout = setTimeout(() => {
        this._tooltipRef = this._overlayRef.attach(new ComponentPortal(CustomTooltipComponent));

        this._tooltipRef.instance.text = this.text;
        this._tooltipRef.instance.contentTemplate = this.contentTemplate;
      }, this.delay);
    }
  }

  /**
   * This method will be called when the mouse goes out of the host element
   * i.e. where this directive is applied
   * This method will close the tooltip by detaching the overlay from the view
   */
  @HostListener('mouseleave')
  private _hide(): void {
    this.closeToolTip();
  }

  /**
   * Destroy lifecycle event handler
   * This method will make sure to close the tooltip
   */
  ngOnDestroy() {
    this.closeToolTip();

    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  /**
   * This method will close the tooltip by detaching the component from the overlay
   */
  private closeToolTip(): void {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }

    if (this._overlayRef) {
      this._overlayRef.detach();
    }

    if (this._tooltipRef) {
      this._tooltipRef.destroy();
    }
  }

}
